import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {MatDialog} from '@angular/material/dialog';
import {from, Observable} from 'rxjs';
import {groupBy, mergeMap, reduce, toArray} from 'rxjs/operators';
import {CaseFuneralDTO} from '../../../../models/CaseFuneralDTO';
import {CaseHospitalDTO} from '../../../../models/case-hospital/CaseHospitalDTO';
import {ClmRequestBuilder} from '../../../../services/clm-request-builder';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {EncounterCaseLinkDTO} from '../../../../models/EncounterCaseLinkDTO';
import {GenericListDialogComponent} from '../../../../../common/dialogs/generic-list-dialog/generic-list-dialog.component';
import {ClaimType} from '../../../../types/claim-type';
import {formatNumber} from '../../../../../common/util/Util';
import {ActivityHistoryDialogComponent} from '../../../dialogs/activity-history-dialog/activity-history-dialog.component';
import {ClmFunctions} from '../../../../common/functions/clm.functions';

enum TableColumns {
  ICONS = 'icons',
  TRANSACTION_ID = 'transactionId',
  TYPE = 'type',
  PROVIDER = 'provider',
  RECEIVED_DATE = 'receivedDate',
  START_DATE = 'startDate', // column -> encounter date
  ASSESSING_STATUS = 'assessingStatus',
  PAY_TO = 'payTo',
  AMOUNT_CLAIMED = 'amountClaimed',
  AMOUNT_BENEFIT = 'amountBenefit',
}

@Component({
  selector: 'app-case-transaction-step',
  templateUrl: './case-transaction-step.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_management-styles.scss',
    '../../../../../../../styles/global/_add-view-edit-styles.scss'
  ]
})
export class CaseTransactionStepComponent implements OnChanges, OnInit {
  readonly dateFormat = DateUtil.dateFormat;
  readonly tableColumns = TableColumns;
  readonly paginatorControls = ClmRequestBuilder.getTableControls();
  readonly ClmFunctions = ClmFunctions;
  readonly ClaimType = ClaimType; // Specifically used for template

  @Input() data: CaseFuneralDTO|CaseHospitalDTO;
  @Input() formGroup: FormGroup;
  @Input() isEditMode: boolean;
  @Output() updateForm: EventEmitter<FormGroup> = new EventEmitter();
  encounterCaseLinDisplayedColumns: string[] = Object.values(TableColumns);
  encounterCaseLinkDataSource: MatTableDataSource<EncounterCaseLinkDTO> = new MatTableDataSource();

  constructor(private dialog: MatDialog) {
  }

  @ViewChild('MatSortTransactionStep') set matSort(ms: MatSort) {
    if (!ms) {
      return;
    }
    this.encounterCaseLinkDataSource.sortingDataAccessor = (item: EncounterCaseLinkDTO, property: string) => {
      const {encounterDTO} = item;
      if (typeof encounterDTO[property] === 'object' && encounterDTO[property] !== null) {
        return encounterDTO[property].name;
      }
      else {
        return encounterDTO[property];
      }
    };
    this.encounterCaseLinkDataSource.sort = ms;
  }

  @ViewChild('MatPaginatorTransactionStep') set matPaginator(mp: MatPaginator) {
    if (!mp) {
      return;
    }
    this.encounterCaseLinkDataSource.paginator = mp;
  }

  get isDataExist() {
    return this.encounterCaseLinkDataSource && (
      this.encounterCaseLinkDataSource.data &&
      this.encounterCaseLinkDataSource.data.length > 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEditMode) {
      this.isEditMode = changes.isEditMode.currentValue;
    }
    if (changes.formGroup) {
      this.formGroup = changes.formGroup.currentValue;
    }
    if (changes.data && changes.data.currentValue) {
      this.data = changes.data.currentValue;
      this.setFormsForEdit(this.data);
    }
  }

  ngOnInit(): void {
    // Notify Updated Reference of FormGroup to parent:
    this.updateForm.next(this.formGroup);
  }

  getPayload() {
    return {
      encounterCases: this.encounterCaseLinkDataSource.data && this.encounterCaseLinkDataSource.data.length > 0 ? this.encounterCaseLinkDataSource.data : null
    };
  }

  showTotalUtilisationPerProvider() {
    const COLUMNS_NAMES = 'Provider;Total';
    const columns: string[] = [COLUMNS_NAMES];
    const data = this.encounterCaseLinkDataSource.data;

    this.groupByItemsBy$(data, 'type')
        .subscribe((data: any[]) => {
          let items = [];
          if (data.length > 1) {

            let claimList: EncounterCaseLinkDTO[] = [];
            let approvalList: EncounterCaseLinkDTO[] = [];

            if (data[0][0].encounterDTO.type === ClaimType.CLAIM) {
              claimList = data[0];
              approvalList = data[1];
            }
            else {
              claimList = data[1];
              approvalList = data[0];
            }

            claimList.forEach((parent: EncounterCaseLinkDTO) => {
              approvalList.forEach((child: EncounterCaseLinkDTO, index) => {
                const isMatched = (
                  (parent.encounterDTO.type === ClaimType.CLAIM && child.encounterDTO.type === ClaimType.APPROVAL) &&
                  (parent.encounterDTO.priorAuthorisationNumber === child.encounterDTO.transactionId.toString())
                );
                if (isMatched) {
                  delete approvalList[index];
                  approvalList = approvalList.filter(item => Boolean(item));
                }
              });
            });
            items = [...claimList, ...approvalList];
          }
          else {
            items = data[0]; // Array of Array is retrieved. So extract array from `0th` index
          }
          this.groupByItemsBy$(items, 'name', true)
              .subscribe((dataItems) => {
                dataItems.forEach(groupByItems => {
                  const displayStr = this.createDisplayStringGenericListDialog(groupByItems);
                  columns.push(displayStr);
                });
              });
        });

    this.dialog.open(GenericListDialogComponent, {
      width: '40vw',
      maxHeight: '80vh',
      data: {list: columns, heading: 'Utilisation Per Provider'},
    });
  }

  viewClaimDetails($event: MouseEvent, transactionId: string) {
    event.stopPropagation();
    this.dialog.open(ActivityHistoryDialogComponent, {
      width: '80vw',
      height: '80vh',
      data: {id: transactionId, field: 'encounterByEncounterId.transactionByTransactionId.transactionId'}
    });
  }

  private groupByItemsBy$(items: EncounterCaseLinkDTO[], property: string, byProvider: boolean = false): Observable<any> {
    const data = from(items);
    return data.pipe(
      groupBy((row: EncounterCaseLinkDTO) => byProvider ? row?.encounterDTO?.provider[property] : row.encounterDTO[property]),
      mergeMap(group => group
        .pipe(
          reduce((acc, cur) => {
            acc.push(cur);
            return acc;
          }, [])
        )
      ),
      toArray()
    );
  }

  private createDisplayStringGenericListDialog(groupByItems: EncounterCaseLinkDTO[]): string {
    if (groupByItems && groupByItems.length > 0) {
      let sum = 0;
      groupByItems.map(item => sum += item.encounterDTO.amountBenefit);
      return `${groupByItems[0].encounterDTO.provider.name};${formatNumber(sum)}`;
    }
    else {
      return 'No details available;';
    }
  }

  private setFormsForEdit(data: CaseFuneralDTO|CaseHospitalDTO) {
    const {encounterCases} = data;
    if (encounterCases && encounterCases.length > 0) {
      this.encounterCaseLinkDataSource.data = encounterCases;   // Assign the data to the data source for the table to render
    }
    else {
      this.encounterCaseLinkDataSource.data = [];
    }
  }
}
